import React, { useState, useEffect } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	TextField,
	Checkbox,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableRow,
	TableHead,
	Paper,
	Pagination,
} from "@mui/material";
import axios from "axios";
import { createPortal } from "react-dom";

const CategorySelectionDialog = ({ open, onClose, onSelect }) => {
	return (
		<Dialog
			open={open}
			onClose={onClose}
			keepMounted
			disablePortal
			aria-labelledby="category-selection-title"
		>
			<div style={{ padding: "20px" }}>
				<h2 id="category-selection-title" className="text-lg font-medium mb-4">
					Select Category
				</h2>
				<div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
					<Button
						variant="contained"
						onClick={() => onSelect("IO")}
						fullWidth
						aria-label="Select IO category"
					>
						IO
					</Button>
					<Button
						variant="contained"
						onClick={() => onSelect("AG")}
						fullWidth
						aria-label="Select AG category"
					>
						AG
					</Button>
				</div>
			</div>
		</Dialog>
	);
};

const MaterialSelectionModal = ({ visible, onClose, onSubmit }) => {
	const [storageLocation, setStorageLocation] = useState("");
	const [materials, setMaterials] = useState([]);
	const [loading, setIsLoading] = useState(true);
	const [searchTerm, setSearchTerm] = useState("");
	const [selectedMaterials, setSelectedMaterials] = useState([]);
	const [page, setPage] = useState(1);
	const [showCategoryDialog, setShowCategoryDialog] = useState(false);
	const [showConfirmDialog, setShowConfirmDialog] = useState(false);
	const [pendingStorageLocation, setPendingStorageLocation] = useState("");
	const itemsPerPage = 10;

	const storageOptions = [
		{ value: "ST11", label: "ST11" },
		{ value: "ST21", label: "ST21" },
	];

	useEffect(() => {
		const fetchMaterials = async () => {
			if (!storageLocation) return;

			setIsLoading(true);
			try {
				const response = await axios.get(
					`${process.env.REACT_APP_API_BASE_URL}/materials/${storageLocation}`,
					{
						headers: {
							Authorization: `Bearer ${localStorage.getItem("token")}`,
						},
					}
				);
				// Transform the data to keep both original and display versions
				const transformedData = response.data.map((material) => ({
					...material,
					displayMatnr: material.matnr.replace(/^0+/, ""), // For display
					displayCharg: material.charg ? material.charg.replace(/^0+/, "") : "", // For display
					matnr: material.matnr, // Keep original with leading zeros
					charg: material.charg || "", // Keep original with leading zeros
				}));
				setMaterials(transformedData);
			} catch (error) {
				console.error("Error fetching materials:", error);
				if (error.response?.status === 401) {
					localStorage.removeItem("token");
					window.location.href = "/login";
				}
			}
			setIsLoading(false);
		};

		fetchMaterials();
	}, [storageLocation]);

	const columns = [
		{ id: "select", label: "Select", width: 70 },
		{ id: "displayMatnr", label: "Material", width: 130 },
		{ id: "maktx", label: "Material Description", width: 200 },
		{ id: "werks", label: "Plant", width: 100 },
		{ id: "lgort", label: "Storage Location", width: 130 },
		{ id: "displayCharg", label: "Batch", width: 100 },
		{ id: "menge", label: "Quantity", width: 100 },
		{ id: "uom", label: "UOM", width: 100 },
	];

	const filteredMaterials = materials.filter(
		(material) =>
			material.maktx.toLowerCase().includes(searchTerm.toLowerCase()) ||
			material.displayMatnr.toLowerCase().includes(searchTerm.toLowerCase())
	);

	// Calculate pagination
	const startIndex = (page - 1) * itemsPerPage;
	const endIndex = startIndex + itemsPerPage;
	const totalPages = Math.ceil(filteredMaterials.length / itemsPerPage);

	// Get current page items
	const currentItems = filteredMaterials.slice(startIndex, endIndex);

	const handleCheckboxChange = (e, row) => {
		if (e.target.checked) {
			// if (selectedMaterials.length >= 2) {
			// 	alert("You can only select up to 2 materials");
			// 	return;
			// }

			// Check if trying to mix V and NV materials
			if (selectedMaterials.length > 0) {
				const existingType = selectedMaterials[0].type;
				if (existingType !== row.type) {
					alert("Cannot mix V and NV type materials");
					return;
				}
			}

			setSelectedMaterials([...selectedMaterials, row]);
		} else {
			setSelectedMaterials(
				selectedMaterials.filter(
					(item) => !(item.matnr === row.matnr && item.charg === row.charg)
				)
			);
		}
	};

	const handlePageChange = (event, value) => {
		setPage(value);
	};

	const handleSubmit = () => {
		if (selectedMaterials.length === 0) return;

		// Add item numbers to selected materials and remove quantity
		const materialsWithItems = selectedMaterials.map((material, index) => ({
			...material,
			item: (index + 1).toString().padStart(2, "0"),
			charg: material.charg || "",
			menge: "", // Set empty quantity
		}));

		const materialType = selectedMaterials[0].type;

		if (materialType === "V") {
			setShowCategoryDialog(true);
		} else {
			onSubmit(materialsWithItems, "NV", null);
			onClose();
		}
	};

	const handleCategorySelect = (category) => {
		setShowCategoryDialog(false);
		const materialsWithItems = selectedMaterials.map((material, index) => ({
			...material,
			item: (index + 1).toString().padStart(2, "0"),
			charg: material.charg || "",
			menge: "", // Set empty quantity
		}));
		onSubmit(materialsWithItems, "V", category);
		onClose();
	};

	const handleStorageLocationChange = (newValue) => {
		if (selectedMaterials.length > 0) {
			setPendingStorageLocation(newValue);
			setShowConfirmDialog(true);
		} else {
			setStorageLocation(newValue);
		}
	};

	const handleConfirmStorageChange = (confirmed) => {
		if (confirmed) {
			setSelectedMaterials([]);
			setStorageLocation(pendingStorageLocation);
		}
		setShowConfirmDialog(false);
		setPendingStorageLocation("");
	};

	const ConfirmationDialog = () => {
		if (!showConfirmDialog) return null;

		return createPortal(
			<div
				className="fixed inset-0 z-[9999] overflow-y-auto"
				role="dialog"
				aria-labelledby="confirm-dialog-title"
				aria-describedby="confirm-dialog-description"
				onClick={(e) => e.stopPropagation()}
			>
				<div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
					<div
						className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
						aria-hidden="true"
						onClick={(e) => e.stopPropagation()}
					/>

					<div
						className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
						onClick={(e) => e.stopPropagation()}
					>
						<div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
							<div className="sm:flex sm:items-start">
								<div className="mt-3 text-center sm:mt-0 sm:text-left">
									<h3
										id="confirm-dialog-title"
										className="text-lg font-medium leading-6 text-gray-900"
									>
										Change Storage Location
									</h3>
									<div className="mt-2">
										<p
											id="confirm-dialog-description"
											className="text-sm text-gray-500"
										>
											Changing storage location will clear all selected
											materials. Do you want to continue?
										</p>
									</div>
								</div>
							</div>
						</div>
						<div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
							<button
								type="button"
								className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
								onClick={() => handleConfirmStorageChange(true)}
								aria-label="Confirm storage location change"
							>
								Yes
							</button>
							<button
								type="button"
								className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
								onClick={() => handleConfirmStorageChange(false)}
								aria-label="Cancel storage location change"
							>
								No
							</button>
						</div>
					</div>
				</div>
			</div>,
			document.getElementById("portal-root")
		);
	};

	return (
		<>
			<Dialog
				open={visible}
				onClose={onClose}
				maxWidth="lg"
				fullWidth
				keepMounted
				disablePortal
				aria-labelledby="material-selection-title"
			>
				<DialogTitle id="material-selection-title" sx={{ pb: 1 }}>
					Select Materials
				</DialogTitle>
				<DialogContent>
					<div className="flex flex-col sm:flex-row gap-4 mb-4 mt-2">
						<FormControl size="small" sx={{ minWidth: 200 }}>
							<InputLabel>Storage Location</InputLabel>
							<Select
								value={storageLocation}
								onChange={(e) => handleStorageLocationChange(e.target.value)}
								label="Storage Location"
							>
								{storageOptions.map((option) => (
									<MenuItem key={option.value} value={option.value}>
										{option.label}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<TextField
							size="small"
							label="Search"
							variant="outlined"
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
							sx={{ flexGrow: 1 }}
						/>
					</div>

					{/* Desktop view */}
					<div className="hidden md:block">
						<TableContainer
							component={Paper}
							sx={{
								maxHeight: 440,
								"& .MuiTableCell-root": {
									py: 1,
									px: 2,
								},
							}}
						>
							<Table stickyHeader size="small">
								<TableHead>
									<TableRow>
										{columns.map((column) => (
											<TableCell
												key={column.id}
												sx={{
													width: column.width,
													backgroundColor: "background.paper",
													fontWeight: "bold",
												}}
											>
												{column.label}
											</TableCell>
										))}
									</TableRow>
								</TableHead>
								<TableBody>
									{loading ? (
										<TableRow>
											<TableCell colSpan={8} align="center">
												Loading...
											</TableCell>
										</TableRow>
									) : (
										currentItems.map((row) => (
											<TableRow
												key={row.matnr + row.charg}
												hover
												className={
													row.type === "NV"
														? "bg-green-50 hover:bg-green-100"
														: ""
												}
											>
												<TableCell padding="checkbox">
													<Checkbox
														checked={selectedMaterials.some(
															(item) =>
																item.matnr === row.matnr &&
																item.charg === row.charg
														)}
														onChange={(e) => handleCheckboxChange(e, row)}
													/>
												</TableCell>
												<TableCell>{row.displayMatnr}</TableCell>
												<TableCell>{row.maktx}</TableCell>
												<TableCell>{row.werks}</TableCell>
												<TableCell>{row.lgort}</TableCell>
												<TableCell>{row.displayCharg}</TableCell>
												<TableCell>{row.menge}</TableCell>
												<TableCell>{row.uom}</TableCell>
											</TableRow>
										))
									)}
								</TableBody>
							</Table>
						</TableContainer>
					</div>

					{/* Mobile view */}
					<div className="md:hidden">
						{loading ? (
							<div className="text-center py-4">Loading...</div>
						) : (
							<div className="space-y-3">
								{currentItems.map((row) => (
									<Paper
										key={row.matnr + row.charg}
										elevation={1}
										sx={{
											bgcolor: row.type === "NV" ? "#f0fdf4" : "white",
										}}
										className={`p-3 ${
											row.type === "NV"
												? "hover:bg-green-100/80"
												: "hover:bg-gray-50"
										}`}
									>
										<div className="flex items-start justify-between mb-2">
											<div className="flex items-center">
												<Checkbox
													checked={selectedMaterials.some(
														(item) =>
															item.matnr === row.matnr &&
															item.charg === row.charg
													)}
													onChange={(e) => handleCheckboxChange(e, row)}
													size="small"
												/>
												<div className="ml-2">
													<div className="font-medium">{row.displayMatnr}</div>
													<div className="text-sm text-gray-600">
														{row.maktx}
													</div>
												</div>
											</div>
											<div className="text-right text-sm">
												<div>
													{row.menge} {row.uom}
												</div>
											</div>
										</div>
										<div className="ml-9 text-sm grid grid-cols-2 gap-2">
											<div>
												<span className="text-gray-500">Plant:</span>{" "}
												{row.werks}
											</div>
											<div>
												<span className="text-gray-500">SLoc:</span> {row.lgort}
											</div>
											{row.displayCharg && (
												<div className="col-span-2">
													<span className="text-gray-500">Batch:</span>{" "}
													{row.displayCharg}
												</div>
											)}
										</div>
									</Paper>
								))}
							</div>
						)}
					</div>

					<div className="flex justify-center mt-4">
						<Pagination
							count={totalPages}
							page={page}
							onChange={handlePageChange}
							size="small"
							shape="rounded"
						/>
					</div>
				</DialogContent>
				<DialogActions sx={{ p: 2 }}>
					<Button onClick={onClose}>Cancel</Button>
					<Button
						onClick={handleSubmit}
						variant="contained"
						color="primary"
						size="small"
						disabled={selectedMaterials.length === 0}
					>
						Submit
					</Button>
				</DialogActions>
			</Dialog>

			<CategorySelectionDialog
				open={showCategoryDialog}
				onClose={() => setShowCategoryDialog(false)}
				onSelect={handleCategorySelect}
			/>

			<ConfirmationDialog />
		</>
	);
};

export default MaterialSelectionModal;
